import moment from 'moment';

/**
 * When a weekday toggle is active in week view, the visible window is a sliding
 * set of 7 weekly occurrences starting at selectedDate, so it reaches ~6 weeks
 * (42 days) past the anchor — well beyond a single calendar week. The fetch
 * window/edge-detection below widen accordingly so every visible occurrence's
 * data is loaded.
 */
export const WEEKDAY_TOGGLE_WINDOW_DAYS = 42;

// Extra days fetched past the visible window end so the next slide's data is
// already loaded before the user reaches it.
export const WEEKDAY_TOGGLE_FETCH_BUFFER_DAYS = 7;

// Check if approaching edge of cached calendar-items data.
export const isApproachingEdge = (
  selectedDate,
  calendarItemsDateRange,
  viewType,
  bufferDays = 7,
  isWeekdayToggleActive = false,
) => {
  // No range cached yet, need to fetch
  if (!calendarItemsDateRange.earliest || !calendarItemsDateRange.latest) {
    return true;
  }

  const current = moment(selectedDate);
  const earliest = moment(calendarItemsDateRange.earliest);
  const latest = moment(calendarItemsDateRange.latest);

  // Check if we're within buffer of edges
  const daysTillStart = current.diff(earliest, 'days');
  const daysTillEnd = latest.diff(current, 'days');

  // For week view, check the entire visible range
  if (viewType === 'weekView') {
    const weekStart = current.clone().startOf('week');
    // For a weekday toggle the window extends ~6 weeks past the anchor
    const weekEnd = isWeekdayToggleActive
      ? current.clone().add(WEEKDAY_TOGGLE_WINDOW_DAYS, 'days')
      : current.clone().endOf('week');
    return weekStart.diff(earliest, 'days') < bufferDays
      || latest.diff(weekEnd, 'days') < bufferDays;
  }

  return daysTillStart < bufferDays || daysTillEnd < bufferDays;
};

// Calculate the padded date range to fetch.
export const calculate69DayPaddedRange = (selectedDate, isWeekdayToggleActive = false) => {
  const reduxDate = moment(selectedDate);

  // For a weekday toggle the visible window is forward-only (anchor + 6 weeks),
  // so fetch from the anchor's week start through the window end plus a buffer.
  if (isWeekdayToggleActive) {
    const windowStart = reduxDate.clone().startOf('week');
    const windowEnd = reduxDate
      .clone()
      .add(WEEKDAY_TOGGLE_WINDOW_DAYS + WEEKDAY_TOGGLE_FETCH_BUFFER_DAYS, 'days')
      .endOf('week');
    return {
      firstDayMonth: windowStart.format('YYYY-MM-DD'),
      lastDayMonth: windowEnd.format('YYYY-MM-DD'),
    };
  }

  // Calculate current month + next month days
  const currentMonthStart = reduxDate.clone().startOf('month');
  const nextMonthEnd = reduxDate.clone().add(1, 'month').endOf('month');
  const currentAndNextMonthDays = nextMonthEnd.diff(currentMonthStart, 'days') + 1;

  // Calculate max previous days such that total <= 69
  const maxPreviousDays = Math.max(0, 69 - currentAndNextMonthDays);

  // Calculate actual start date
  const fetchStartDate = currentMonthStart.clone().subtract(maxPreviousDays, 'days');

  return {
    firstDayMonth: fetchStartDate.format('YYYY-MM-DD'),
    lastDayMonth: nextMonthEnd.format('YYYY-MM-DD'),
  };
};

// Whether recurring-items data needs a re-fetch for the current window.
export const isApproachingNewRecurringRange = (
  selectedDate,
  recurringItemsDateRange,
  isWeekdayToggleActive = false,
) => {
  if (!recurringItemsDateRange?.earliest || !recurringItemsDateRange?.latest) {
    return true;
  }

  const { earliest, latest } = recurringItemsDateRange;
  const selectedDateStr = moment(selectedDate).format('YYYY-MM-DD');

  if (selectedDateStr < earliest) return true;

  // For a weekday toggle, refetch before the far end of the window scrolls out
  if (isWeekdayToggleActive) {
    const windowEndStr = moment(selectedDate)
      .add(WEEKDAY_TOGGLE_WINDOW_DAYS, 'days')
      .format('YYYY-MM-DD');
    return windowEndStr >= latest;
  }

  if (selectedDateStr >= latest) return true;

  return false;
};
